import { MiniGrid } from "./MiniGrid";
import { Money, StatePill, ZoneBadge } from "./primitives";
import { fmtMicro, shortAddr, slotCount, symbolFor, isPartialListing, displayParcelId, type ZoneKey } from "./mkt-format";
import { listingCells } from "./useMarketplace";
import { GenesisBadge } from "../genesis/GenesisBadge";
import { useGenesisParcel } from "../genesis/useGenesis";
import { useT } from "../../i18n";
import type { Listing, MarketplaceConfig } from "../../lib/marketplace-api";

const ZONE_FILL: Record<ZoneKey, string> = {
  standard: "fill-zone-standard", premium: "fill-zone-premium", vip: "fill-zone-vip",
  reserve: "fill-zone-reserve", grid_zero: "fill-neon-coral", unknown: "fill-neon-teal",
};

export function LandCard({
  listing,
  zone,
  config,
  onOpen,
  testId = "land-card",
}: {
  listing: Listing;
  zone: ZoneKey;
  config?: MarketplaceConfig | null;
  onOpen: () => void;
  testId?: string;
}) {
  const t = useT();
  const cells = listingCells(listing);
  const sym = symbolFor(listing.currency, config);
  const slots = slotCount(cells);
  const { member: genesis } = useGenesisParcel(displayParcelId(listing));
  return (
    <button
      type="button"
      data-testid={testId}
      data-order-hash={listing.order_hash}
      onClick={onOpen}
      className="group text-left neon-glass rounded-lg border border-border-divider overflow-hidden hover:border-neon-teal/40 hover:shadow-glow-teal-sm transition-[border-color,box-shadow] duration-fast"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-elevated/40">
        <MiniGrid cells={cells} zoneClass={ZONE_FILL[zone]} />
        <div className="absolute top-2.5 left-2.5"><ZoneBadge zone={zone} /></div>
        <div className="absolute top-2.5 right-2.5"><StatePill state={listing.status} /></div>
        {genesis?.is_genesis && (
          <div className="absolute bottom-2.5 left-2.5"><GenesisBadge member={genesis} size="sm" testId="land-card-genesis-badge" /></div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 font-display text-base text-text-primary">
            {t("marketplace.common.parcelNumber", { id: displayParcelId(listing) })}
            {isPartialListing(listing) && (
              <span data-testid="land-card-partial-badge" className="font-mono text-[8.5px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border border-neon-coral/50 text-neon-coral">{t("marketplace.common.partialTag")}</span>
            )}
          </span>
          <span className="font-mono text-[10px] text-text-muted">{t("marketplace.land.slots", { n: slots })}</span>
        </div>
        <div className="flex items-end justify-between gap-2">
          <Money amount={fmtMicro(listing.price_micro)} symbol={sym} className="text-lg text-neon-teal" />
          <span className="font-mono text-[10px] text-text-muted">{shortAddr(listing.seller)}</span>
        </div>
      </div>
    </button>
  );
}
